import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ADD_TRIP,
  DELETE_TRIP,
  GET_TRIP,
  GET_TRIP_SHARES,
  GET_TRIPS,
  REVOKE_TRIP_SHARE,
  SEARCH_DESTINATIONS,
  SHARE_TRIP,
  UPDATE_TRIP,
} from './operations';
import TravelingForm from './TravelingForm';
import ShareTripModal from './ShareTripModal';
import PremiumUpsellModal from '../../components/PremiumUpsellModal';
import ModuleListSkeleton from '../../components/common/ModuleListSkeleton';
import DetailActionMenu from '../../components/common/DetailActionMenu';
import EntryImage from '../../components/common/EntryImage';
import HobbyEmptyState from '../../components/common/HobbyEmptyState';
import { GET_MY_SUBSCRIPTION_STATUS } from '../../views/account/operations';
import { getPublicErrorMessage, isLimitReachedError } from '../../utils/errors';
import { canCreateEntries } from '../../utils/subscriptionLimits';
import { moduleCommonStrings, travelingStrings } from '../../content/hobbyModules';
import { shareHighlightStrings } from '../../content/shareHighlights';
import { buildShareHighlight, openShareHighlight } from '../shareHighlights/shareHighlight';
import { fileToBase64 } from '../../components/EntryPhotoPicker';
import { ADD_ENTRY_HIGHLIGHT_PHOTO, REMOVE_ENTRY_COVER_PHOTO, SAVE_ENTRY_PHOTO_ALBUM_LINK, UPLOAD_ENTRY_COVER_PHOTO } from '../entryPhotos/operations';
import { saveCreatedAlbumLink } from '../entryPhotos/albumDraft';
import { entryPhotoStrings } from '../../content/entryPhotos';
import PhotoAlbumsSection from '../entryPhotos/PhotoAlbumsSection';
import EntryChecklistProgress from '../checklists/EntryChecklistProgress';

const TRIPS_PAGE_SIZE = 20;

/**
 * Performs the access level for operation.
 *
 * @param {*} item - item value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const accessLevelFor = (item) => item?.accessLevel || 'owner';
/**
 * Determines whether edit access.
 *
 * @param {*} item - item value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
const canEditAccess = (item) => ['owner', 'edit'].includes(accessLevelFor(item));
/**
 * Determines whether owner access.
 *
 * @param {*} item - item value.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
const isOwnerAccess = (item) => accessLevelFor(item) === 'owner';
/**
 * Performs the shared access label operation.
 *
 * @param {*} accessLevel - access level value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const sharedAccessLabel = (accessLevel) => moduleCommonStrings.sharing.accessLine(accessLevel);
/**
 * Performs the shared by label operation.
 *
 * @param {string} name - name value.
 * @returns {void} No value; the operation applies its documented side effects.
 */
const sharedByLabel = (name) => moduleCommonStrings.sharing.sharedBy(name);

const defaultTrip = {
  title: '',
  destination: '',
  startDate: '',
  endDate: '',
  status: 'Idea',
  tripType: 'Solo',
  countries: [],
  cities: [],
  tags: [],
  coverUrl: '',
  coverPhotoUrl: '',
  coverPhotoUpdatedAt: '',
  favorite: false,
  notes: '',
  itinerary: [],
  places: [],
  expenses: [],
  packingItems: [],
  linkedHikes: [],
};

/**
 * Performs the split csv operation.
 *
 * @param {*} value - Value to inspect or transform.
 * @returns {*} The derived operation result.
 */
function splitCsv(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean);
}

/**
 * Normalizes trip.
 *
 * @param {*} trip - trip value.
 * @returns {*} The derived operation result.
 */
function parseTrip(trip) {
  const itinerary = Array.isArray(trip.itinerary) ? trip.itinerary : [];
  return {
    ...defaultTrip,
    ...trip,
    title: trip.title || trip.destination || travelingStrings.module.untitled,
    countries: Array.isArray(trip.countries) ? trip.countries : splitCsv(trip.countries),
    cities: Array.isArray(trip.cities) ? trip.cities : splitCsv(trip.cities),
    tags: Array.isArray(trip.tags) ? trip.tags : splitCsv(trip.tags),
    favorite: Boolean(trip.favorite),
    itinerary: itinerary.map((day, index) => ({
      id: day?.id || `day_${index + 1}`,
      date: day?.date || '',
      title: day?.title || `Day ${index + 1}`,
      notes: day?.notes || '',
      items: Array.isArray(day?.items) ? day.items : [],
    })),
    places: Array.isArray(trip.places) ? trip.places : [],
    expenses: Array.isArray(trip.expenses) ? trip.expenses : [],
    packingItems: Array.isArray(trip.packingItems) ? trip.packingItems : [],
    linkedHikes: Array.isArray(trip.linkedHikes) ? trip.linkedHikes : [],
  };
}

/**
 * Performs the unique values operation.
 *
 * @param {Array<*>} items - items collection.
 * @param {*} getter - getter value.
 * @returns {*} The derived operation result.
 */
function uniqueValues(items, getter) {
  return Array.from(new Set(items.flatMap(getter).filter(Boolean))).sort();
}

/**
 * Normalizes nested list.
 *
 * @param {Array<*>} items - items collection.
 * @returns {*} The derived operation result.
 */
function cleanNestedList(items = []) {
  return items.filter((item) => item.title || item.name).map((item) => ({ ...item }));
}

/**
 * Normalizes input.
 *
 * @param {*} formData - form data value.
 * @returns {*} The derived operation result.
 */
function cleanInput(formData) {
  return {
    title: formData.title.trim(),
    destination: formData.destination.trim(),
    startDate: formData.startDate,
    endDate: formData.endDate,
    status: formData.status,
    tripType: formData.tripType,
    countries: splitCsv(formData.countries),
    cities: splitCsv(formData.cities),
    tags: splitCsv(formData.tags),
    coverUrl: formData.coverUrl.trim(),
    favorite: Boolean(formData.favorite),
    notes: formData.notes.trim(),
    itinerary: (formData.itinerary || []).filter((day) => day.date || day.title || day.notes || day.items?.length).map((day) => ({
      id: day.id,
      date: day.date,
      title: day.title,
      notes: day.notes || '',
      items: cleanNestedList(day.items || []),
    })),
    places: cleanNestedList(formData.places || []),
    expenses: cleanNestedList(formData.expenses || []).map((expense) => ({
      ...expense,
      amount: Number(expense.amount || 0),
    })),
    packingItems: (formData.packingItems || []).filter((item) => item.title).map((item) => ({ ...item, packed: Boolean(item.packed) })),
  };
}

/**
 * Performs the trip date label operation.
 *
 * @param {*} trip - trip value.
 * @returns {*} The derived operation result.
 */
function tripDateLabel(trip) {
  return [trip.startDate, trip.endDate].filter(Boolean).join(' - ') || travelingStrings.module.datesNotSet;
}

/**
 * Performs the trip day title operation.
 *
 * @param {*} day - day value.
 * @param {number} index - index numeric value.
 * @returns {*} The derived operation result.
 */
function tripDayTitle(day, index) {
  const defaultTitle = `Day ${index + 1}`;
  const customTitle = String(day.title || '').trim();
  const normalized = customTitle.toLowerCase();
  return customTitle && normalized !== 'day' && normalized !== defaultTitle.toLowerCase()
    ? `${defaultTitle} · ${customTitle}`
    : defaultTitle;
}

/** Owns trip lists, route-based create/detail flows, sharing, filters, and mutations. */
export default function TravelingModule({
  isAdding,
  setIsAdding,
  theme,
  onClose,
  onRegisterCloseGuard,
  onHeaderActionsChange,
  onHeaderTitleChange,
  modulePath = '',
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetTripId = searchParams.get('tripId');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [editingTrip, setEditingTrip] = useState(null);
  const [tripPendingDelete, setTripPendingDelete] = useState(null);
  const [sharingTrip, setSharingTrip] = useState(null);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [search, setSearch] = useState('');
  const [activeStatus, setActiveStatus] = useState('');
  const [activeAccess, setActiveAccess] = useState('');
  const [activeTag, setActiveTag] = useState('');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [shareSuccess, setShareSuccess] = useState('');
  const [showPremiumUpsell, setShowPremiumUpsell] = useState(false);
  const hasFormChangesRef = useRef(false);
  const closeFormRef = useRef(null);
  const routeParts = useMemo(
    () => String(modulePath || '').split('/').map((part) => decodeURIComponent(part)).filter(Boolean),
    [modulePath]
  );
  const isCreateRoute = routeParts[0] === 'new';
  const routeTripId = routeParts[0] && routeParts[0] !== 'new' ? routeParts[0] : '';
  const isEditRoute = Boolean(routeTripId && routeParts[1] === 'edit');
  const isAddFlow = isAdding || isCreateRoute;
  const subscriptionRefetch = [{ query: GET_MY_SUBSCRIPTION_STATUS }];

  const { loading, error, data, fetchMore } = useQuery(GET_TRIPS, {
    variables: { first: TRIPS_PAGE_SIZE, after: null },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  });
  const { data: subscriptionData } = useQuery(GET_MY_SUBSCRIPTION_STATUS, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: shareData } = useQuery(GET_TRIP_SHARES, {
    variables: { tripId: sharingTrip?.id || '' },
    skip: !sharingTrip,
  });
  const [loadTripDetail] = useLazyQuery(GET_TRIP, { fetchPolicy: 'network-only' });
  const [searchDestinations, {
    data: destinationData,
    loading: isSearchingDestinations,
    error: destinationSearchError,
  }] = useLazyQuery(SEARCH_DESTINATIONS);
  const parsedTrips = useMemo(() => (data?.getTripsListPage?.nodes || []).map(parseTrip), [data]);
  const tripsPageInfo = data?.getTripsListPage?.pageInfo || {};
  const subscription = subscriptionData?.getMySubscriptionStatus;
  const statuses = useMemo(() => uniqueValues(parsedTrips, (trip) => [trip.status]), [parsedTrips]);
  const tags = useMemo(() => uniqueValues(parsedTrips, (trip) => trip.tags), [parsedTrips]);
  const destinationResults = destinationData?.searchDestinations || [];

  const filteredTrips = useMemo(() => {
    const query = search.trim().toLowerCase();
    return parsedTrips.filter((trip) => {
      const searchable = [
        trip.title,
        trip.destination,
        trip.status,
        trip.tripType,
        trip.tags.join(' '),
        trip.notes,
      ].join(' ').toLowerCase();

      return (
        (!query || searchable.includes(query)) &&
        (!activeStatus || trip.status === activeStatus) &&
        (!activeAccess || (activeAccess === 'shared' ? (trip.accessLevel || 'owner') !== 'owner' : (trip.accessLevel || 'owner') === 'owner')) &&
        (!activeTag || trip.tags.includes(activeTag)) &&
        (!favoritesOnly || trip.favorite)
      );
    });
  }, [activeAccess, activeStatus, activeTag, favoritesOnly, parsedTrips, search]);

  const [addTrip, { loading: isSaving }] = useMutation(ADD_TRIP, {
    refetchQueries: [{ query: GET_TRIPS, variables: { first: TRIPS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setHasFormChanges(false);
    },
  });
  const [saveAlbumLink] = useMutation(SAVE_ENTRY_PHOTO_ALBUM_LINK);
  const [addHighlightPhoto] = useMutation(ADD_ENTRY_HIGHLIGHT_PHOTO);

  const [updateTrip, { loading: isUpdating }] = useMutation(UPDATE_TRIP, {
    refetchQueries: [{ query: GET_TRIPS, variables: { first: TRIPS_PAGE_SIZE, after: null } }],
    onCompleted: (result) => {
      const updated = parseTrip(result.updateTrip);
      setEditingTrip(null);
      setHasFormChanges(false);
      setSelectedTrip(updated);
    },
  });

  const [deleteTrip, { loading: isDeleting }] = useMutation(DELETE_TRIP, {
    refetchQueries: [{ query: GET_TRIPS, variables: { first: TRIPS_PAGE_SIZE, after: null } }, ...subscriptionRefetch],
    onCompleted: () => {
      setTripPendingDelete(null);
      setSelectedTrip(null);
      navigate('/hobby/traveling');
    },
  });

  const [shareTrip, { loading: isSharing, error: shareError }] = useMutation(SHARE_TRIP, {
    refetchQueries: (result) => [
      { query: GET_TRIPS, variables: { first: TRIPS_PAGE_SIZE, after: null } },
      { query: GET_TRIP_SHARES, variables: { tripId: result.data?.shareTrip?.itemId || sharingTrip?.id } },
    ],
    onCompleted: (result) => {
      const share = result.shareTrip;
      setShareSuccess(share.status === 'pending'
        ? `Invite pending for ${share.granteeEmail}.`
        : `${share.granteeDisplayName || share.granteeEmail} now has ${share.permission === 'edit' ? 'edit' : 'view'} access.`);
    },
  });

  const [revokeTripShare, { loading: isRevoking }] = useMutation(REVOKE_TRIP_SHARE, {
    refetchQueries: [{ query: GET_TRIP_SHARES, variables: { tripId: sharingTrip?.id || '' } }],
    onCompleted: () => setShareSuccess('Access removed.'),
  });
  const [uploadEntryCoverPhoto, { loading: isUploadingPhoto }] = useMutation(UPLOAD_ENTRY_COVER_PHOTO);
  const [removeEntryCoverPhoto, { loading: isRemovingPhoto }] = useMutation(REMOVE_ENTRY_COVER_PHOTO);

  /**
   * Performs trip photo.
   *
   * @param {*} formData - form data value.
   * @param {string} tripId - trip id value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const syncTripPhoto = async (formData, tripId) => {
    if (!tripId) return;
    const refetchQueries = [{ query: GET_TRIPS, variables: { first: TRIPS_PAGE_SIZE, after: null } }];

    if (formData.coverPhotoFile) {
      await uploadEntryCoverPhoto({
        variables: {
          input: {
            hobbyId: 'traveling',
            entryId: tripId,
            contentType: formData.coverPhotoFile.type || 'image/jpeg',
            base64Data: await fileToBase64(formData.coverPhotoFile),
          },
        },
        refetchQueries,
        awaitRefetchQueries: true,
      });
      return;
    }

    if (formData.shouldRemoveCoverPhoto) {
      await removeEntryCoverPhoto({
        variables: { hobbyId: 'traveling', entryId: tripId },
        refetchQueries,
        awaitRefetchQueries: true,
      });
    }
  };

  /**
   * Handles save.
   *
   * @param {*} formData - form data value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSave = async (formData) => {
    const input = cleanInput(formData);
    try {
      if (formData.id) {
        await updateTrip({ variables: { id: formData.id, input } });
        await syncTripPhoto(formData, formData.id);
        const detail = await fetchTripDetail({ id: formData.id, ...input });
        setSelectedTrip(detail);
        navigate(`/hobby/traveling/${encodeURIComponent(formData.id)}`);
        return;
      }
      const result = await addTrip({ variables: { input } });
      const entryId = result.data?.addTrip?.id;
      await syncTripPhoto(formData, entryId);
      for (const file of formData.highlightDrafts || []) {
        try {
          await addHighlightPhoto({ variables: { input: { hobbyId: 'traveling', entryId, contentType: file.type, base64Data: await fileToBase64(file), visibility: 'owner' } } });
        } catch (photoError) {
          window.alert(`${entryPhotoStrings.tripSavedWithoutPhoto} ${getPublicErrorMessage(photoError)}`);
          break;
        }
      }
      try { await saveCreatedAlbumLink(saveAlbumLink, 'traveling', entryId, formData.albumDraft); }
      catch { window.alert(entryPhotoStrings.tripSavedWithoutAlbum); }
      navigate('/hobby/traveling');
      onClose();
    } catch (saveError) {
      if (isLimitReachedError(saveError)) {
        setShowPremiumUpsell(true);
        return;
      }
      window.alert(getPublicErrorMessage(saveError));
    }
  };

  const handleSearchDestinations = useCallback((query) => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < 2) return;
    searchDestinations({ variables: { query: trimmedQuery } });
  }, [searchDestinations]);

  const closeForm = useCallback(() => {
    const editingTripId = editingTrip?.id;
    setEditingTrip(null);
    setHasFormChanges(false);
    navigate(editingTripId ? `/hobby/traveling/${encodeURIComponent(editingTripId)}` : '/hobby/traveling');
    onClose();
  }, [editingTrip?.id, navigate, onClose]);

  const handleCancelForm = useCallback(({ confirmDiscard } = {}) => {
    if (confirmDiscard && hasFormChanges) {
      const shouldDiscard = window.confirm('Discard your unsaved trip changes?');
      if (!shouldDiscard) return;
    }
    closeForm();
  }, [closeForm, hasFormChanges]);

  useEffect(() => {
    hasFormChangesRef.current = hasFormChanges;
  }, [hasFormChanges]);

  useEffect(() => {
    closeFormRef.current = closeForm;
  }, [closeForm]);

  /**
   * Handles confirm delete.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleConfirmDelete = async () => {
    if (!tripPendingDelete) return;
    await deleteTrip({ variables: { id: tripPendingDelete.id } });
  };

  /**
   * Handles share trip.
   *
   * @param {object} options - Behavioral options for the operation.
   * @param {*} options.granteeEmail - grantee email value.
   * @param {*} options.permission - permission value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleShareTrip = async ({ granteeEmail, permission }) => {
    if (!sharingTrip) return;
    setShareSuccess('');
    await shareTrip({ variables: { input: { tripId: sharingTrip.id, granteeEmail, permission } } });
  };

  /**
   * Handles revoke share.
   *
   * @param {string} id - Stable identifier of the target resource.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleRevokeShare = async (id) => {
    setShareSuccess('');
    await revokeTripShare({ variables: { id } });
  };

  /**
   * Handles load more.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleLoadMore = async () => {
    if (!tripsPageInfo.hasNextPage || !tripsPageInfo.endCursor) return;
    await fetchMore({
      variables: {
        first: TRIPS_PAGE_SIZE,
        after: tripsPageInfo.endCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getTripsListPage) return previousResult;
        return {
          getTripsListPage: {
            ...fetchMoreResult.getTripsListPage,
            nodes: [
              ...(previousResult.getTripsListPage?.nodes || []),
              ...(fetchMoreResult.getTripsListPage.nodes || []),
            ],
          },
        };
      },
    });
  };

  /**
   * Determines whether start create flow.
   *
   * @returns {boolean} Whether the requested condition is satisfied.
   */
  const canStartCreateFlow = () => {
    if (canCreateEntries(subscription, 'traveling')) return true;
    setShowPremiumUpsell(true);
    return false;
  };

  /**
   * Handles start add.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleStartAdd = () => {
    if (!canStartCreateFlow()) return;
    setIsAdding(true);
    navigate('/hobby/traveling/new');
  };

  useEffect(() => {
    if (isAddFlow || editingTrip) {
      onHeaderActionsChange?.(null);
      return () => onHeaderActionsChange?.(null);
    }

    if (selectedTrip) {
      const canEditTrip = canEditAccess(selectedTrip);
      const isOwner = isOwnerAccess(selectedTrip);

      onHeaderActionsChange?.(
        <DetailActionMenu
          label="Trip actions"
          primaryAction={canEditTrip ? { label: 'Edit', onSelect: () => handleEditTrip(selectedTrip) } : null}
          actions={[
            isOwner && { label: shareHighlightStrings.action, onSelect: () => openShareHighlight(buildShareHighlight('traveling', selectedTrip)) },
            isOwner && { label: shareHighlightStrings.manageAccess, onSelect: () => handleShareSelectedTrip(selectedTrip) },
            isOwner && { label: travelingStrings.module.deleteAction, variant: 'danger', onSelect: () => setTripPendingDelete(selectedTrip) },
          ]}
        />
      );
      return () => onHeaderActionsChange?.(null);
    }

    onHeaderActionsChange?.(
      <div className="recipe-actions">
        <button className="btn-primary" type="button" onClick={handleStartAdd}>{travelingStrings.form.addTitle}</button>
      </div>
    );
    return () => onHeaderActionsChange?.(null);
  }, [editingTrip, isAddFlow, onHeaderActionsChange, selectedTrip, subscription]);

  useEffect(() => {
    if (isEditRoute || editingTrip) {
      onHeaderTitleChange?.(travelingStrings.module.editAction);
    } else if (isCreateRoute || isAdding) {
      onHeaderTitleChange?.(travelingStrings.form.addTitle);
    } else if (routeTripId || selectedTrip) {
      onHeaderTitleChange?.(selectedTrip?.title || 'Trip');
    } else {
      onHeaderTitleChange?.('');
    }

    return () => onHeaderTitleChange?.('');
  }, [editingTrip, isAdding, isCreateRoute, isEditRoute, onHeaderTitleChange, routeTripId, selectedTrip]);

  /**
   * Renders premium upsell.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const renderPremiumUpsell = () => showPremiumUpsell && (
    <PremiumUpsellModal
      hobbyId="traveling"
      subscription={subscription}
      onClose={() => setShowPremiumUpsell(false)}
      onOpenAccount={() => {
        setShowPremiumUpsell(false);
        window.location.assign('/account');
      }}
    />
  );

  const fetchTripDetail = useCallback(async (trip) => {
    const result = await loadTripDetail({ variables: { id: trip.id } });
    return parseTrip(result.data?.getTrip || trip);
  }, [loadTripDetail]);

  useEffect(() => {
    if (isCreateRoute) {
      setSelectedTrip(null);
      setEditingTrip(null);
      setIsAdding(true);
      return;
    }

    if (!routeTripId) {
      setSelectedTrip(null);
      setEditingTrip(null);
      setIsAdding(false);
      return;
    }

    setIsAdding(false);
    const trip = parsedTrips.find((item) => item.id === routeTripId) || { id: routeTripId };
    fetchTripDetail(trip)
      .then((detail) => {
        if (isEditRoute) {
          if (!canEditAccess(detail)) {
            setEditingTrip(null);
            setSelectedTrip(detail);
            navigate(`/hobby/traveling/${encodeURIComponent(detail.id)}`, { replace: true });
            return;
          }
          setSelectedTrip(null);
          setEditingTrip(detail);
          return;
        }
        setEditingTrip(null);
        setSelectedTrip(detail);
      })
      .catch(() => navigate('/hobby/traveling', { replace: true }));
  }, [fetchTripDetail, isCreateRoute, isEditRoute, navigate, parsedTrips, routeTripId, setIsAdding]);

  useEffect(() => {
    if (!targetTripId) return;
    navigate(`/hobby/traveling/${encodeURIComponent(targetTripId)}`, { replace: true });
  }, [navigate, targetTripId]);

  /**
   * Handles select trip.
   *
   * @param {*} trip - trip value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSelectTrip = async (trip) => {
    setSelectedTrip(trip);
    navigate(`/hobby/traveling/${encodeURIComponent(trip.id)}`);
  };

  /**
   * Handles edit trip.
   *
   * @param {*} trip - trip value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleEditTrip = async (trip) => {
    if (!canEditAccess(trip)) return;
    navigate(`/hobby/traveling/${encodeURIComponent(trip.id)}/edit`);
    const detail = await fetchTripDetail(trip);
    if (!canEditAccess(detail)) {
      setSelectedTrip(detail);
      setEditingTrip(null);
      navigate(`/hobby/traveling/${encodeURIComponent(detail.id)}`, { replace: true });
      return;
    }
    setEditingTrip(detail);
  };

  /**
   * Handles share selected trip.
   *
   * @param {*} trip - trip value.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleShareSelectedTrip = async (trip) => {
    setSharingTrip(await fetchTripDetail(trip));
  };

  useEffect(() => {
    if (isAddFlow || editingTrip) {
      onRegisterCloseGuard?.(() => {
        if (hasFormChangesRef.current) {
          const shouldDiscard = window.confirm('Discard your unsaved trip changes?');
          if (!shouldDiscard) return;
        }
        closeFormRef.current?.();
      });
      return () => onRegisterCloseGuard?.(null);
    }
    if (selectedTrip) {
      onRegisterCloseGuard?.(() => navigate('/hobby/traveling'));
      return () => onRegisterCloseGuard?.(null);
    }
    onRegisterCloseGuard?.(null);
    return undefined;
  }, [editingTrip, isAddFlow, navigate, onRegisterCloseGuard, selectedTrip]);

  if (isAddFlow || editingTrip) {
    return (
      <>
        <TravelingForm
          theme={theme}
          onCancel={handleCancelForm}
          onSave={handleSave}
          initialData={editingTrip}
          onDirtyChange={setHasFormChanges}
          destinationResults={destinationResults}
          onSearchDestinations={handleSearchDestinations}
          isSearchingDestinations={isSearchingDestinations}
          destinationSearchError={destinationSearchError}
          isEditing={Boolean(editingTrip)}
          onLimitReached={() => setShowPremiumUpsell(true)}
          isSaving={isSaving || isUpdating || isUploadingPhoto || isRemovingPhoto}
        />
        {renderPremiumUpsell()}
      </>
    );
  }

  if (selectedTrip) {
    const selectedAccessLevel = accessLevelFor(selectedTrip);

    return (
      <div className="module-wrapper traveling-module movies-module">
        <div className="recipe-detail-view">
          <button type="button" className="recipe-back-link" onClick={() => navigate('/hobby/traveling')}>← Back</button>

          {sharingTrip && (
            <ShareTripModal
              trip={sharingTrip}
              shares={shareData?.getTripShares || []}
              onClose={() => setSharingTrip(null)}
              onShare={handleShareTrip}
              onRevoke={handleRevokeShare}
              isSharing={isSharing}
              isRevoking={isRevoking}
              error={shareError}
              successMessage={shareSuccess}
            />
          )}

          {tripPendingDelete && (
            <div className="modal-overlay">
              <div className="modal-content delete-confirmation">
                <p className="eyebrow">{travelingStrings.module.deleteTitle}</p>
                <h2>{travelingStrings.module.deletePrompt}</h2>
                <p>{travelingStrings.module.deleteDetail(tripPendingDelete.title)}</p>
                <div className="form-actions">
                  <button type="button" className="btn-ghost" onClick={() => setTripPendingDelete(null)}>{moduleCommonStrings.actions.cancel}</button>
                  <button type="button" className="btn-danger solid" onClick={handleConfirmDelete} disabled={isDeleting}>
                    {isDeleting ? moduleCommonStrings.actions.deleting : travelingStrings.module.deleteAction}
                  </button>
                </div>
              </div>
            </div>
          )}

          <section className="recipe-detail-hero">
            <EntryImage
              className="entry-detail-photo"
              src={selectedTrip.coverPhotoUrl || selectedTrip.coverUrl}
              title={selectedTrip.destination || selectedTrip.title}
              icon="✈"
              fallbackType="traveling"
              variant="detail-square"
            />
            <div>
              <p className="eyebrow">{selectedTrip.destination || travelingStrings.module.detailsMissing}</p>
              <h2>{selectedTrip.title}</h2>
            </div>
          </section>

          {selectedAccessLevel !== 'owner' && (
            <div className={`shared-access-banner ${selectedAccessLevel === 'edit' ? 'editable' : ''}`}>
              <strong>{sharedAccessLabel(selectedAccessLevel)}</strong>
              <span>{sharedByLabel(selectedTrip.sharedByName)}</span>
            </div>
          )}

          <div className="trip-detail-grid">
            <PhotoAlbumsSection hobbyId="traveling" entryId={selectedTrip.id} isOwner={selectedAccessLevel === 'owner'} canManage={false} onLimitReached={() => setShowPremiumUpsell(true)} />
            <section className="recipe-detail-panel">
              <h3>{travelingStrings.module.detailsTitle}</h3>
              <div className="detail-info-rows">
                {[
                  [travelingStrings.form.fields.status, selectedTrip.status],
                  [travelingStrings.form.fields.tripType, selectedTrip.tripType],
                  [travelingStrings.module.dates, tripDateLabel(selectedTrip)],
                ].filter(([, value]) => value).map(([label, value]) => (
                  <div className="detail-info-row" key={label}>
                    <span>{label}</span>
                    <strong>{value}</strong>
                  </div>
                ))}
              </div>
            </section>

            <section className="recipe-detail-panel">
              <h3>{travelingStrings.module.itineraryTitle}</h3>
              <div className="trip-summary-list">
                {selectedTrip.itinerary.map((day, index) => (
                  <div key={day.id}>
                    <strong>{tripDayTitle(day, index)}</strong>
                    {day.date && <span>{day.date}</span>}
                    {day.notes && <span>{day.notes}</span>}
                  </div>
                ))}
                {selectedTrip.itinerary.length === 0 && <p className="detail-notes">{travelingStrings.module.noItinerary}</p>}
              </div>
            </section>

            <section className="recipe-detail-panel">
              <h3>{travelingStrings.module.packingTitle}</h3>
              <div className="trip-summary-list">
                <EntryChecklistProgress hobbyId="traveling" entryId={selectedTrip.id} items={selectedTrip.packingItems} canEdit={canEditAccess(selectedTrip)} onChanged={async () => setSelectedTrip(await fetchTripDetail(selectedTrip))} />
                {selectedTrip.packingItems.length === 0 && <p className="detail-notes">{travelingStrings.module.noPackingItems}</p>}
              </div>
            </section>

            <section className="recipe-detail-panel">
              <h3>{travelingStrings.module.notesTitle}</h3>
              <p className="detail-notes">{selectedTrip.notes || travelingStrings.module.noNotes}</p>
            </section>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="module-wrapper traveling-module movies-module">
      <div className="recipe-library-layout">
        <aside className="recipe-books-panel">
          <p className="eyebrow">{travelingStrings.module.tripStatus}</p>
          <div className="recipe-book-list">
            <button type="button" className={!activeStatus ? 'active' : ''} onClick={() => setActiveStatus('')}>
              <span>{travelingStrings.module.allTrips}</span>
              <strong>{parsedTrips.length}</strong>
            </button>
            {statuses.map((status) => (
              <button type="button" key={status} className={activeStatus === status ? 'active' : ''} onClick={() => setActiveStatus(status)}>
                <span>{status}</span>
                <strong>{parsedTrips.filter((trip) => trip.status === status).length}</strong>
              </button>
            ))}
          </div>
        </aside>

        <section className="recipe-main-panel">
          <div className="recipe-filters">
            <div className="form-group search-field">
              <label>{travelingStrings.module.searchLabel}</label>
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={travelingStrings.module.searchPlaceholder} />
            </div>
            <div className="filter-row trip-filter-row">
              <label>
                {travelingStrings.module.tagLabel}
                <select value={activeTag} onChange={(event) => setActiveTag(event.target.value)}>
                  <option value="">{travelingStrings.module.anyTag}</option>
                  {tags.map((tag) => <option key={tag} value={tag}>{tag}</option>)}
                </select>
              </label>
              <button type="button" className={`favorite-filter ${favoritesOnly ? 'active' : ''}`} onClick={() => setFavoritesOnly((current) => !current)}>
                Favorites
              </button>
            </div>
            <div className="view-toggle access-filter" role="group" aria-label={travelingStrings.module.accessFilterLabel}>
              <button type="button" className={!activeAccess ? 'active' : ''} onClick={() => setActiveAccess('')}>{travelingStrings.module.accessAll}</button>
              <button type="button" className={activeAccess === 'owned' ? 'active' : ''} onClick={() => setActiveAccess('owned')}>{travelingStrings.module.accessOwned}</button>
              <button type="button" className={activeAccess === 'shared' ? 'active' : ''} onClick={() => setActiveAccess('shared')}>{travelingStrings.module.accessShared}</button>
            </div>
          </div>

          {loading && parsedTrips.length === 0 && (
            <div className="module-loading-state">
              <ModuleListSkeleton />
            </div>
          )}
          {error && <p className="error-copy">Error: {getPublicErrorMessage(error)}</p>}

          <div className="recipe-stats">
            <span>{travelingStrings.module.shown(filteredTrips.length)}</span>
            <span>{travelingStrings.module.favorites(parsedTrips.filter((trip) => trip.favorite).length)}</span>
            <span>{travelingStrings.module.active(parsedTrips.filter((trip) => ['Planning', 'Booked', 'In progress'].includes(trip.status)).length)}</span>
            <span>{travelingStrings.module.shared(parsedTrips.filter((trip) => (trip.accessLevel || 'owner') !== 'owner').length)}</span>
          </div>

          <div className="recipe-card-grid">
            {filteredTrips.map((trip) => (
              <button key={trip.id} type="button" className="recipe-card trip-card has-entry-photo" onClick={() => handleSelectTrip(trip)}>
                <EntryImage
                  src={trip.coverPhotoUrl || trip.coverUrl}
                  title={trip.destination || trip.title}
                  icon="✈"
                  fallbackType="traveling"
                />
                <div>
                  <div className="movie-card-heading">
                    <h3>{trip.title}</h3>
                    {trip.favorite && <span className="favorite-chip">{travelingStrings.module.favorite}</span>}
                  </div>
                  <p>{trip.destination || travelingStrings.module.detailsMissing}</p>
                  <p className="movie-card-detail-line">
                    {[tripDateLabel(trip), trip.status].filter(Boolean).join(' • ')}
                  </p>
                  {trip.accessLevel && trip.accessLevel !== 'owner' && (
                    <p className="shared-card-line">{sharedAccessLabel(trip.accessLevel)}</p>
                  )}
                </div>
              </button>
            ))}
          </div>

          {tripsPageInfo.hasNextPage && (
            <div className="load-more-row">
              <button type="button" className="btn-secondary" onClick={handleLoadMore} disabled={loading}>
                {loading ? travelingStrings.module.loadingMore : travelingStrings.module.loadMore}
              </button>
            </div>
          )}

          {filteredTrips.length === 0 && !loading && (
            <HobbyEmptyState hobbyId="traveling" filtered={parsedTrips.length > 0}
              title={parsedTrips.length === 0 ? moduleCommonStrings.empty.nothingSaved : moduleCommonStrings.empty.noMatches}
              detail={parsedTrips.length === 0 ? travelingStrings.module.emptyFirst : moduleCommonStrings.filters.tryDifferentSearchOrFilter} />
          )}
        </section>
      </div>
      {renderPremiumUpsell()}
    </div>
  );
}
